import { useTranslation } from 'react-i18next';
import { Tooltip, MenuItem } from '@material-ui/core';
import SelectUi from '@mui/material/Select';
import { Controller, UseFormReturn } from 'react-hook-form';
import { ListValueProps, selectStyle } from '../utils';
import { InputProjectSimulator, SelectProjectSimulator } from '../ProjectSimulator';

type TypologyProps = {
  typology: TypologyData;
  form: UseFormReturn;
};

export type TypologyData = {
  parking: {
    number: number;
    type: {
      list: {
        EXTERIOR: ListValueProps;
      };
      value: number;
    };
  };
  buildingTypeId: {
    list: number[];
    value: number;
  };
  balconies: number;
  usefulArea: number;
  plotArea: number;
};

function Typology({ typology, form }: TypologyProps) {
  const { t } = useTranslation();
  const parkingTypeList: ListValueProps[] = Object.values(typology.parking.type.list);
  const parkingTypeValue: number = form.watch('parkingType') ?? typology.parking.type.value;
  const parkingTypeIsNone = parkingTypeValue === typology.parking.type.list.NONE.id 

  return (
    <div className="form-block" style={{ marginTop: '12px', paddingBottom: '32px' }}>
      <h2 className="form-title">
        {t('APP.PROJECT.SIMULATOR.QUICK_SIMULATION.TYPOLOGY.TITLE', {
          defaultValue: 'Typologie',
        })}
      </h2>

      <hr className="line-title-separation" />

      <div
        style={{
          display: 'flex',
          paddingLeft: '24px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px',
          }}
        >
          <div>
            <label
              className="form-label"
              style={{
                display: 'flex',
              }}
              htmlFor="typology"
            >
              {t('APP.PROJECT.SIMULATOR.QUICK_SIMULATION.FORM.LABEL.TYPOLOGY', {
                defaultValue: 'Typologie',
              })}
            </label>
            <Controller
              control={form.control}
              name="buildingTypeId"
              defaultValue={typology.buildingTypeId.value}
              render={({ field }) => (
                <Tooltip
                  title={t(
                    'APP.PROJECT.SIMULATOR.QUICK_SIMULATION.TYPOLOGY.UNAVAILABLE_TOOLTIP',
                    { defaultValue: "D'autres choix seront bientôt disponibles" }
                  )}
                >
                  <SelectUi
                    {...field}
                    sx={selectStyle}
                    disabled={true}
                    value={typology.buildingTypeId.list[0]}
                    style={{ width: '160px' }}
                  >
                    <MenuItem value={typology.buildingTypeId.value}>
                      {t('APP.PROJECT.SIMULATOR.QUICK_SIMULATION.TYPOLOGY.SELECT', {
                        defaultValue: 'Bureaux',
                      })}
                    </MenuItem>
                  </SelectUi>
                </Tooltip>
              )}
            />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              columnGap: '16px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '26%',
              }}
            >
              <label className="form-label">
                {t('APP.PROJECT.SIMULATOR.QUICK_SIMULATION.FORM.LABEL.PARCEL_AREA', {
                  defaultValue: 'Surface parcelle (m²)',
                })}
              </label>
              <InputProjectSimulator
                form={form}
                name="plotArea"
                defaultValue={typology.plotArea}
                style={{ width: '65px' }}
                hideValueIfFormNotPatched={true}
              />
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '29%',
              }}
            >
              <label className="form-label">
                {t('APP.PROJECT.SIMULATOR.QUICK_SIMULATION.FORM.USABLE_AREA', {
                  defaultValue: 'Surface utile (m²)',
                })}
              </label>
              <InputProjectSimulator
                form={form}
                name="usefulArea"
                defaultValue={typology.usefulArea}
                style={{ width: '65px' }}
                hideValueIfFormNotPatched={true}
              />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', columnGap: '32px' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label className="form-label" htmlFor="parking">
                {t('APP.PROJECT.SIMULATOR.QUICK_SIMULATION.FORM.LABEL.PARKING', {
                  defaultValue: 'Stationnements',
                })}
              </label>
              <SelectProjectSimulator
                form={form}
                name="parkingType"
                defaultValue={typology.parking.type.value}
                listValues={parkingTypeList}
                style={{ width: '160px' }}
              />
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <label className="form-label">
                {t(
                  'APP.PROJECT.SIMULATOR.QUICK_SIMULATION.FORM.LABEL.PARKING_SPACES',
                  {
                    defaultValue: 'Nombre de places',
                  }
                )}
              </label>
              <InputProjectSimulator
                disabled={parkingTypeIsNone}
                form={form}
                name="parkingNumbers"
                defaultValue={typology.parking.number}
                style={{ width: '65px' }}
              />
            </div>
          </div>

          <div
            style={{
              display: 'flex',
            }}
          >
            <p
              style={{
                marginRight: '10px',
              }}
              className="typology-text-other-area"
            >
              {t('APP.PROJECT.SIMULATOR.QUICK_SIMULATION.TEXT.OTHER_AREA', {
                defaultValue: 'Autres surfaces',
              })}{' '}
              :
            </p>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <label className="typology-label-balcony">
                {t('APP.PROJECT.SIMULATOR.QUICK_SIMULATION.FORM.LABEL.BALCONY', {
                  defaultValue: 'Balcons (m²)',
                })}
              </label>
              <InputProjectSimulator
                form={form}
                name="balconies"
                defaultValue={typology.balconies}
                style={{ width: '65px' }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Typology;
